import { TranslationKey } from './../../../../types/translation/TranslationKey';
import React from 'react';
import { BinStatus } from '../../../../types/bin';
import { Product } from '../../../../types/product';
import { ProductMasterData } from '../../../../types/product_master_data/productMasterData';
import { TagRelation } from '../../../../types/tag/tagRelation';
import { t } from '../../../../types/translation/Translator';
import { ProductIcon } from '../../../icons/Product/ProductIcon';
import { BinFilter } from '../BinFilter/BinFilter';
import { BaseFilter, BaseFilterProps } from '../common/BaseFilter';
import { FilterStorageKey, FilterProps } from '../common/filter.util';
import { StockLocationFilter } from '../StockLocationFilter';
import { TagFilter } from '../TagFilter';
import { Bin } from '../../../../types/bin';
import { CustomFieldFilter } from '../CustomFieldFilter';
import { CustomField, CustomFieldEntityType } from '../../../../types/custom_field/customField';
import { ProductMasterDataFilter } from '../ProductMasterDataFilter';
import { itemsContainText } from '../../../../util/string.util';
import { BinStatusFilter } from '../BinFilter';
import { EntityType } from '../../../../types/comment';
import { productTableFilter } from '../../../../ui/components/Table/Product/ProductTable';
import { TableSettings } from '../../../../types/table_settings/TableSettings';
import { Company } from '../../../../types/company';
import { CompanyPartnerFilter } from '../CompanyPartnerFilter/CompanyPartnerFilter';
import { StockLocation } from '../../../../types/stock_location';
import { ContainerType } from '../../../../types/container_type';
import ContainerFilter from '../ContainerFilter';
import { Container } from '../../../../types/container';

export enum ProductFilterType {
  group = 'group',
  detail = 'detail',
}

export class ProductFilter extends BaseFilter<Product> {
  stockLocations: Map<string, StockLocation>;
  productMasterData: Map<string, ProductMasterData>;
  bins: Map<string, Bin>;
  binStatuses: Map<string, BinStatus>;
  containers: Map<string, Container>;
  containerTypes: Map<string, ContainerType>;
  tagRelations: Map<string, TagRelation>;
  customFields: Map<string, CustomField>;
  products: Map<string, Product>;
  productTableSettings: TableSettings<any>;
  containerTableSettings: TableSettings<any>;
  type: ProductFilterType;
  company: Company;

  constructor(
    stockLocations: Map<string, StockLocation>,
    productMasterData: Map<string, ProductMasterData>,
    bins: Map<string, Bin>,
    binStatuses: Map<string, BinStatus>,
    containers: Map<string, Container>,
    containerTypes: Map<string, ContainerType>,
    tagRelations: Map<string, TagRelation>,
    customFields: Map<string, CustomField>,
    products: Map<string, Product>,
    productTableSettings: TableSettings<any>,
    containerTableSettings: TableSettings<any>,
    type: ProductFilterType,
    company: Company,
  ) {
    super(FilterStorageKey.PRODUCT);
    this.stockLocations = stockLocations;
    this.productMasterData = productMasterData;
    this.bins = bins;
    this.binStatuses = binStatuses;
    this.containers = containers;
    this.containerTypes = containerTypes;
    this.tagRelations = tagRelations;
    this.customFields = customFields;
    this.products = products;
    this.productTableSettings = productTableSettings;
    this.containerTableSettings = containerTableSettings;
    this.type = type;
    this.company = company;
    this.stockLocations = stockLocations;
  }

  filterOptions = () => [
    ...(this.company.partner ? [new CompanyPartnerFilter()] : []),
    new ProductMasterDataFilter(this.customFields, this.tagRelations, this.company, this.productTableSettings),
    new StockLocationFilter(this.tagRelations, this.company),
    new TagFilter(EntityType.stockLocation),
    new TagFilter(EntityType.pmd),
    new BinFilter(this.binStatuses, this.tagRelations, this.customFields, this.bins, this.company, this.stockLocations),
    new BinStatusFilter(),
    new ContainerFilter(
      this.stockLocations,
      this.bins,
      this.containerTypes,
      this.binStatuses,
      this.tagRelations,
      this.customFields,
      this.containers,
      this.containerTableSettings,
      this.company,
    ),
    ...CustomFieldFilter.for(CustomFieldEntityType.bin, this.bins, this.customFields),
    ...CustomFieldFilter.for(CustomFieldEntityType.productMasterData, this.productMasterData, this.customFields),
    ...CustomFieldFilter.for(CustomFieldEntityType.container, this.containers, this.customFields),
  ];

  toLabel = () => {
    return t(TranslationKey.product).singular.label;
  };

  toCount(props: BaseFilterProps) {
    return props.filterProps.product?.size || 0;
  }

  toDropdownContent() {
    return <></>;
  }

  toInnerContent = () => {
    return <></>;
  };

  toIcon = () => {
    return <ProductIcon className={this.className} />;
  };

  search = (item: Product, query: string) => {
    switch (this.type) {
      case ProductFilterType.detail:
        const productName = this.productMasterData.get(item.productMasterDataId)?.productName;
        const productNumber = this.productMasterData.get(item.productMasterDataId)?.productNumber;
        const bin = this.bins.get(item.binId)?.name;
        const container = item.containerId ? this.containers.get(item.containerId)?.identifier : undefined;
        return itemsContainText(query, [productName, productNumber, bin, item.lpn, item.serial, container]);
      case ProductFilterType.group:
        return productTableFilter(item, query, this.productTableSettings, this.productMasterData, this.containers);
    }
  };

  filteredItems(items: Product[], query: string, filterProps: FilterProps): Product[] {
    return items.filter(item => {
      const bin = this.bins.get(item.binId);
      const pmd = this.productMasterData.get(item.productMasterDataId);
      const container = item.containerId ? this.containers.get(item.containerId) : undefined;

      return [
        ProductMasterDataFilter.filter(filterProps, pmd, this.customFields, this.tagRelations),
        StockLocationFilter.filter(filterProps, this.stockLocations.get(item.stockLocationId)),
        TagFilter.filterBy(filterProps, EntityType.stockLocation, this.tagRelations, item.stockLocationId),
        TagFilter.filterBy(filterProps, EntityType.pmd, this.tagRelations, item.productMasterDataId),
        BinFilter.filter(filterProps, bin, this.customFields),
        !container && filterProps.container?.size
          ? false
          : ContainerFilter.filter(filterProps, container, this.customFields),
        ProductFilter.filter(filterProps, item, this.customFields),
        BinStatusFilter.filter(filterProps),
        this.search(item, query),
      ].every(value => value === null || value);
    });
  }

  static filter(filterProps: FilterProps, item?: Product, customFields: Map<string, CustomField> = new Map()) {
    if (item && filterProps.company?.size && !filterProps.company.has(item.companyId)) return false;
    return true;
  }
}
